import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Slider from "react-slick";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import { MoreProducts } from "web/react/components/product-gallery/product-gallery-more-products/product-gallery-more-products";
import "web/react/components/product-gallery/slick-theme.less";
import "web/react/components/product-gallery/slick.less";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Text } from "web/react/emo/text";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useInStockProductPageContext } from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";

import {
    Image,
    ProductGalleryImage,
} from "web/react/pages/product/product-image-gallery/product-gallery-image";
import { sendAnalyticsEvent } from "web/react/pages/product/product-image-gallery/utils";
import { getSafariMajorVersion } from "web/react/utils/user-agent";
import { gettext } from "web/script/modules/django-i18n";
import * as styles from "./product-image-gallery-fullscreen.css";
import { ThumbnailStrip } from "./thumbnail-strip";
import { ZoomableSlide, ZoomDirection } from "./zoomable-slide";

interface ProductImageGalleryFullscreenProps {
    images: Image[];
    initialSlideIndex?: number;
    onClose: () => void;
    productId: string;
    buyOptionId: string;
}

interface ArrowButtonProps {
    variant: "prev" | "next";
    className?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
    isMobile?: boolean;
    disabled?: boolean;
}

function ArrowButton({
    variant,
    className,
    onClick,
    onKeyDown,
    isMobile = false,
    disabled = false,
}: ArrowButtonProps): React.ReactElement {
    const rotation = variant === "prev" ? 90 : -90;
    const ariaLabel =
        variant === "prev"
            ? gettext("product_image_gallery_fullscreen.previous_image")
            : gettext("product_image_gallery_fullscreen.next_image");
    const baseStyle = isMobile
        ? styles.mobileNavigationArrow
        : variant === "prev"
        ? styles.prevArrow
        : styles.nextArrow;

    return (
        <button
            className={clsx(baseStyle, className)}
            onClick={onClick}
            onKeyDown={onKeyDown}
            aria-label={ariaLabel}
            disabled={disabled}
            tabIndex={0}
        >
            <SVGIcon name="chevron-thin" rotate={rotation} className={styles.arrowIcon} />
        </button>
    );
}

function PrevArrow(props: Omit<ArrowButtonProps, "variant">): React.ReactElement {
    return <ArrowButton {...props} variant="prev" />;
}

function NextArrow(props: Omit<ArrowButtonProps, "variant">): React.ReactElement {
    return <ArrowButton {...props} variant="next" />;
}

const LOG = (...args: unknown[]): void =>
    console.warn("[Fullscreen]", new Date().toISOString().slice(11, 23), ...args);

export function ProductImageGalleryFullscreen({
    images,
    initialSlideIndex = 0,
    onClose,
    productId,
    buyOptionId,
}: ProductImageGalleryFullscreenProps): React.ReactElement {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(initialSlideIndex);
    const [isImageZoomed, setIsImageZoomed] = useState(false);
    const [isTapHidden, setIsTapHidden] = useState(false);
    const isUIHidden = isImageZoomed || isTapHidden;
    const { isMobileViewport, isTabletViewport } = useDomViewport();
    // Ref (not state) so it's synchronously readable inside event handlers without
    // causing re-renders. Set true from beforeChange until afterChange so that any
    // phantom zoom events QuickPinchZoom fires while a slide is mid-animation are ignored.
    const isSlideTransitioningRef = useRef(false);

    const setIsImageZoomedSafe = useCallback((zoomed: boolean) => {
        LOG(
            `setIsImageZoomedSafe — zoomed=${zoomed}, isTransitioning=${isSlideTransitioningRef.current}`
        );
        if (!isSlideTransitioningRef.current) {
            setIsImageZoomed(zoomed);
        }
    }, []);

    useEffect(() => {
        LOG(`isImageZoomed changed → ${isImageZoomed}`);
    }, [isImageZoomed]);

    useEffect(() => {
        LOG(`isTapHidden changed → ${isTapHidden}`);
    }, [isTapHidden]);

    useEffect(() => {
        LOG(
            `isUIHidden changed → ${isUIHidden} (isImageZoomed=${isImageZoomed}, isTapHidden=${isTapHidden})`
        );
    }, [isUIHidden, isImageZoomed, isTapHidden]);

    useEffect(() => {
        LOG(`currentSlideIndex changed → ${currentSlideIndex}`);
    }, [currentSlideIndex]);

    const sliderRef = useRef<Slider | null>(null);

    const { relatedProducts } = useInStockProductPageContext();
    const showMoreProducts = isMobileViewport && relatedProducts && relatedProducts.length > 0;

    const isIOS18 = getSafariMajorVersion() === 18;

    const totalSlides = images.length + (showMoreProducts ? 1 : 0);
    const canGoToPrevious = currentSlideIndex > 0;
    const canGoToNext = currentSlideIndex < totalSlides - 1;

    const currentImagePosition = currentSlideIndex + 1;

    const onImageZoom = useCallback(
        (direction: ZoomDirection) => {
            sendAnalyticsEvent(`zoomed_${direction}`, true, productId, buyOptionId);
        },
        [productId, buyOptionId]
    );

    const onImageClick = useCallback(() => {
        LOG(
            `onImageClick — isMobile=${isMobileViewport} isTablet=${isTabletViewport} isTapHidden=${isTapHidden}`
        );
        if (isMobileViewport || isTabletViewport) {
            if (!isTapHidden) {
                sendAnalyticsEvent("removed_ui_elements", true, productId, buyOptionId);
            }
            LOG(`onImageClick — calling setIsTapHidden(${!isTapHidden})`);
            setIsTapHidden(!isTapHidden);
        }
    }, [productId, buyOptionId, isTapHidden, isMobileViewport, isTabletViewport]);

    const handlePreviousClick = useCallback(() => {
        if (canGoToPrevious) {
            sliderRef.current?.slickPrev();
        }
    }, [canGoToPrevious]);

    const handleNextClick = useCallback(() => {
        if (canGoToNext) {
            sliderRef.current?.slickNext();
        }
    }, [canGoToNext]);

    return ReactDOM.createPortal(
        <div data-testid="fullscreen-gallery" className={styles.overlay} tabIndex={-1}>
            {!isUIHidden ? (
                <div className={styles.topControls}>
                    {images.length > 0 && currentSlideIndex < images.length ? (
                        <div
                            className={styles.imagePositionIndicator}
                            aria-label={gettext(
                                "product_image_gallery.invisible_text_for_accessibility.position_indicator",
                                {
                                    current_image_position: currentImagePosition,
                                    image_length: images.length,
                                }
                            )}
                        >
                            <Text as="span" textStyle="body-s">
                                {`${currentImagePosition}/${images.length}`}
                            </Text>
                        </div>
                    ) : null}
                    <span className={styles.closeButton}>
                        <CloseButton onClick={onClose} />
                    </span>
                </div>
            ) : null}
            <Slider
                ref={sliderRef}
                speed={400}
                slidesToShow={1}
                centerMode={false}
                infinite={false}
                arrows={!isUIHidden}
                swipe
                touchMove
                draggable={false}
                dots={false}
                beforeChange={(oldIndex, newIndex) => {
                    LOG(`beforeChange — ${oldIndex} → ${newIndex}`);
                    isSlideTransitioningRef.current = true;
                    setIsImageZoomed(false); // immediately restore UI if phantom zoom slipped through
                    setCurrentSlideIndex(newIndex);
                    setIsTapHidden(false);
                }}
                afterChange={(index) => {
                    LOG(`afterChange — settled on slide ${index}`);
                    isSlideTransitioningRef.current = false;
                }}
                prevArrow={<PrevArrow />}
                nextArrow={<NextArrow />}
                className={styles.slider}
                initialSlide={initialSlideIndex}
            >
                {images.map((image, index) => (
                    <div key={`${image.id}-${index}`} className={styles.zoomContainer}>
                        <ZoomableSlide
                            index={index}
                            currentSlideIndex={currentSlideIndex}
                            onZoom={onImageZoom}
                            onZoomStateChange={setIsImageZoomedSafe}
                            onClick={onImageClick}
                        >
                            <ProductGalleryImage
                                src={image.url}
                                alt={image.altText ?? undefined}
                                lazy={!isIOS18}
                                isFullscreen
                                blurImageOnLoad={!isIOS18}
                                tabIndex={-1}
                            />
                        </ZoomableSlide>
                    </div>
                ))}
                {showMoreProducts && (
                    <MoreProducts
                        key="related-slide"
                        products={relatedProducts.slice(0, 4)}
                        fullscreen
                    />
                )}
            </Slider>
            {!isUIHidden ? (
                <>
                    <div className={styles.mobileThumbnailNavigation}>
                        <PrevArrow
                            onClick={handlePreviousClick}
                            disabled={!canGoToPrevious}
                            isMobile
                        />
                        <ThumbnailStrip
                            images={images}
                            currentSlideIndex={currentSlideIndex}
                            onThumbnailImageClick={(index) => sliderRef.current?.slickGoTo(index)}
                            isMobileInline
                            moreProductsImages={
                                showMoreProducts
                                    ? relatedProducts
                                          .slice(0, 4)
                                          .flatMap((p) => (p.image_url ? [p.image_url] : []))
                                    : undefined
                            }
                        />
                        <NextArrow onClick={handleNextClick} disabled={!canGoToNext} isMobile />
                    </div>
                    <div className={styles.desktopThumbnailNavigation}>
                        <ThumbnailStrip
                            images={images}
                            currentSlideIndex={currentSlideIndex}
                            onThumbnailImageClick={(index) => sliderRef.current?.slickGoTo(index)}
                        />
                    </div>
                </>
            ) : null}
        </div>,
        document.body
    );
}
